import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { useDesignSubmissions } from '../../../contexts/DesignSubmissionsContext';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(2deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const wave = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const sway = keyframes`
  0%, 100% { transform: rotate(-2deg) translateY(0); transform-origin: top center; }
  50% { transform: rotate(2deg) translateY(-3px); transform-origin: top center; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f3ea;
  /* Leave room for fixed nav + stacked announcement/event banners */
  padding-top: calc(198px + env(safe-area-inset-top, 0px));

  @media (max-width: 768px) {
    padding-top: calc(172px + env(safe-area-inset-top, 0px));
  }
`;

const Hero = styled.div`
  background: linear-gradient(180deg, #0a1628 0%, #12284a 60%, #162040 100%);
  padding: 5rem 1.5rem 6rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200%;
    height: 80px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 80'%3E%3Cpath d='M0,40 C150,80 350,0 600,40 C850,80 1050,0 1200,40 L1200,80 L0,80 Z' fill='%23f8f3ea'/%3E%3C/svg%3E") repeat-x bottom;
    background-size: 1200px 80px;
    animation: ${wave} 8s linear infinite;
  }
`;

const HeroStars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  &::before, &::after {
    content: '✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦';
    position: absolute;
    color: rgba(255,255,255,0.06);
    font-size: 1.2rem;
    letter-spacing: 3rem;
    white-space: nowrap;
  }
  &::before { top: 1.5rem; }
  &::after { top: 5rem; left: 5rem; }
`;

const HeroEmoji = styled.div`
  font-size: 5rem;
  margin-bottom: 1rem;
  animation: ${sway} 3s ease-in-out infinite;
  transform-origin: top center;
  display: inline-block;
  filter: drop-shadow(0 4px 16px rgba(0,0,0,0.5));
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(245, 200, 66, 0.15);
  border: 1px solid rgba(245, 200, 66, 0.35);
  border-radius: 50px;
  padding: 0.4rem 1.1rem;
  color: #f5c842;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.25rem;
`;

const HeroTitle = styled.h1`
  color: white;
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 700;
  margin: 0 0 0.5rem;
  line-height: 1.05;
  text-shadow: 0 2px 20px rgba(0,0,0,0.4);
`;

const HeroAccent = styled.span`
  color: #f5c842;
`;

const HeroSubtitle = styled.p`
  color: rgba(255,255,255,0.75);
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  max-width: 600px;
  margin: 0.75rem auto 2.5rem;
  line-height: 1.65;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.25s ease;

  &.gold {
    background: linear-gradient(135deg, #f5c842, #e8a820);
    color: #3d2800;
    box-shadow: 0 4px 16px rgba(245, 200, 66, 0.35);
    &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(245, 200, 66, 0.45); }
  }

  &.outline {
    background: rgba(255,255,255,0.1);
    color: white;
    border: 2px solid rgba(255,255,255,0.3);
    &:hover { background: rgba(255,255,255,0.2); border-color: white; transform: translateY(-2px); }
  }
`;

const ContentArea = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem 5rem;
`;

const PrizeStrip = styled.div`
  background: linear-gradient(135deg, #0a1628, #12284a);
  border-radius: 20px;
  padding: 1.75rem 2rem;
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  box-shadow: 0 4px 20px rgba(10, 22, 40, 0.15);
`;

const PrizeEmoji = styled.div`
  font-size: 3rem;
  animation: ${float} 2.5s ease-in-out infinite;
  flex-shrink: 0;
`;

const PrizeText = styled.div`
  flex: 1;
`;

const PrizeTitle = styled.h3`
  color: #f5c842;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  margin: 0 0 0.25rem;
  font-weight: 700;
`;

const PrizeDesc = styled.p`
  color: rgba(255,255,255,0.75);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PrizeAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #f5c842, #e8a820);
  color: #3d2800;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(245,200,66,0.4); }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.1rem 1.5rem;
  box-shadow: 0 2px 10px rgba(16, 29, 64, 0.06);
  border: 1px solid rgba(16, 29, 64, 0.06);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatEmoji = styled.div`
  font-size: 1.75rem;
`;

const StatNumber = styled.div`
  font-size: 1.65rem;
  font-weight: 800;
  color: #101d40;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #999;
  font-weight: 500;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.25rem;
  font-weight: 700;
  color: #101d40;
  margin: 0;
`;

const SectionCount = styled.span`
  background: #101d40;
  color: white;
  border-radius: 50px;
  padding: 0.2rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 1.75rem;
  animation: ${fadeIn} 0.5s ease both;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(16, 29, 64, 0.07), 0 1px 3px rgba(16, 29, 64, 0.04);
  border: 1px solid rgba(16, 29, 64, 0.07);
  transition: all 0.3s ease;
  cursor: pointer;
  animation: ${fadeIn} 0.5s ease both;
  animation-delay: ${props => (props.index || 0) * 0.05}s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 32px rgba(16, 29, 64, 0.13), 0 2px 8px rgba(16, 29, 64, 0.06);
  }
`;

const CardImageWrap = styled.div`
  width: 100%;
  padding-top: 66%;
  position: relative;
  background: #f0ebe0;
  overflow: hidden;
`;

const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CardFlagPole = styled.div`
  position: absolute;
  top: 0;
  left: 16px;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #8b6914, #5c4510);
  border-radius: 2px;
  opacity: 0.6;
  z-index: 1;
`;

const CardBody = styled.div`
  padding: 1.1rem 1.25rem 1.35rem;
`;

const CardTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #101d40;
  margin: 0 0 0.2rem;
  line-height: 1.2;
`;

const CardArtist = styled.p`
  color: #9d6d49;
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 0.55rem;
`;

const CardDescription = styled.p`
  color: #777;
  font-size: 0.85rem;
  margin: 0 0 0.875rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardDate = styled.span`
  color: #ccc;
  font-size: 0.77rem;
`;

const NewBadge = styled.span`
  background: linear-gradient(135deg, #f5c842, #e8a820);
  color: #3d2800;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 10, 20, 0.93);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease both;
`;

const LightboxContent = styled.div`
  background: white;
  border-radius: 24px;
  overflow: hidden;
  max-width: 820px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6);
`;

const LightboxImage = styled.img`
  width: 100%;
  max-height: 58vh;
  object-fit: contain;
  background: #f5f0e5;
`;

const LightboxInfo = styled.div`
  padding: 1.5rem 2rem 2rem;
`;

const LightboxTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  color: #101d40;
  margin: 0 0 0.2rem;
`;

const LightboxArtist = styled.p`
  color: #9d6d49;
  font-weight: 600;
  margin: 0 0 0.75rem;
`;

const LightboxDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin: 0 0 1rem;
  font-size: 0.95rem;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,0.12);
  border: 2px solid rgba(255,255,255,0.25);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10000;

  &:hover { background: rgba(255,255,255,0.25); }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 2rem;
`;

const EmptyEmoji = styled.div`
  font-size: 5rem;
  margin-bottom: 1.5rem;
  animation: ${sway} 3s ease-in-out infinite;
  transform-origin: top center;
  display: inline-block;
`;

const EmptyTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.75rem;
  color: #101d40;
  margin: 0 0 1rem;
`;

const EmptyText = styled.p`
  color: #888;
  font-size: 1.05rem;
  max-width: 480px;
  margin: 0 auto 2rem;
  line-height: 1.65;
`;

const EmptyButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #0a1628, #12284a);
  color: white;
  padding: 1rem 2.25rem;
  border-radius: 14px;
  font-weight: 700;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.25s ease;

  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(10,22,40,0.3); }
`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const isNew = (iso) => Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;

const ChildrensArtGallery = () => {
  const { approved, loading: galleryLoading, error: galleryError } = useDesignSubmissions();
  const [selected, setSelected] = useState(null);

  return (
    <PageContainer>
      <Hero>
        <HeroStars />
        <HeroEmoji>🏴‍☠️</HeroEmoji>
        <HeroBadge>🏴‍☠️ Pirate Circus · Sail Design Competition</HeroBadge>
        <HeroTitle>
          The Pirate <HeroAccent>Sail Gallery</HeroAccent>
        </HeroTitle>
        <HeroSubtitle>
          These are the brave pirates competing to have their sail hoisted on our
          real pirate ship at Belvoir Castle's adventure playground.
          Could yours be next?
        </HeroSubtitle>
        <HeroActions>
          <HeroButton to="/childrens-art-gallery/submit" className="gold">
            🏴‍☠️ Enter the Competition
          </HeroButton>
          <HeroButton to="/pirate-trail" className="outline">
            Find Out More
          </HeroButton>
        </HeroActions>
      </Hero>

      <ContentArea>
        <PrizeStrip>
          <PrizeEmoji>🏆</PrizeEmoji>
          <PrizeText>
            <PrizeTitle>Grand Prizes: Sail &amp; BOOM Circus!</PrizeTitle>
            <PrizeDesc>
              The winning sail design will be sewn into a genuine sail and hoisted from
              the mast of our pirate ship in the Belvoir Castle adventure playground for the Pirate Circus season.
              The winner also receives a <strong>family ticket</strong> to Belvoir Castle <strong>BOOM Circus</strong>.
            </PrizeDesc>
          </PrizeText>
          <PrizeAction to="/childrens-art-gallery/submit">
            🎨 Submit Your Design
          </PrizeAction>
        </PrizeStrip>

        {galleryLoading && (
          <p style={{ textAlign: 'center', color: '#888', padding: '3rem 0' }}>
            Loading entries…
          </p>
        )}

        {galleryError && (
          <p style={{ textAlign: 'center', color: '#dc2626', padding: '2rem 0' }}>
            ⚠️ Could not load gallery: {galleryError}
          </p>
        )}

        {!galleryLoading && approved.length > 0 && (
          <StatsRow>
            <StatCard>
              <StatEmoji>🏴‍☠️</StatEmoji>
              <div>
                <StatNumber>{approved.length}</StatNumber>
                <StatLabel>Entries Received</StatLabel>
              </div>
            </StatCard>
            <StatCard>
              <StatEmoji>⭐</StatEmoji>
              <div>
                <StatNumber>
                  {approved.filter(s => isNew(s.reviewedAt || s.submittedAt)).length}
                </StatNumber>
                <StatLabel>New This Week</StatLabel>
              </div>
            </StatCard>
          </StatsRow>
        )}

        {!galleryLoading && (approved.length === 0 ? (
          <EmptyState>
            <EmptyEmoji>🏴‍☠️</EmptyEmoji>
            <EmptyTitle>No Entries Yet, Matey!</EmptyTitle>
            <EmptyText>
              The competition has just begun! Be the first brave pirate to enter
              your sail design. Could yours be the one that flies on the ship?
            </EmptyText>
            <EmptyButton to="/childrens-art-gallery/submit">
              🏴‍☠️ Be the First to Enter!
            </EmptyButton>
          </EmptyState>
        ) : (
          <>
            <SectionHeader>
              <SectionTitle>Competition Entries</SectionTitle>
              <SectionCount>
                {approved.length} {approved.length === 1 ? 'entry' : 'entries'}
              </SectionCount>
            </SectionHeader>
            <Grid>
              {approved.map((item, i) => (
                <Card key={item.id} index={i} onClick={() => setSelected(item)}>
                  <CardImageWrap>
                    <CardFlagPole />
                    <CardImage src={item.imageUrl} alt={item.title} loading="lazy" />
                  </CardImageWrap>
                  <CardBody>
                    <CardTitle>"{item.title}"</CardTitle>
                    <CardArtist>by {item.childName}, age {item.age}</CardArtist>
                    {item.description && (
                      <CardDescription>{item.description}</CardDescription>
                    )}
                    <CardMeta>
                      <CardDate>{formatDate(item.reviewedAt || item.submittedAt)}</CardDate>
                      {isNew(item.reviewedAt || item.submittedAt) && (
                        <NewBadge>✨ New Entry</NewBadge>
                      )}
                    </CardMeta>
                  </CardBody>
                </Card>
              ))}
            </Grid>
          </>
        ))}
      </ContentArea>

      {selected && (
        <Lightbox onClick={() => setSelected(null)}>
          <LightboxClose onClick={() => setSelected(null)}>✕</LightboxClose>
          <LightboxContent onClick={e => e.stopPropagation()}>
            <LightboxImage src={selected.imageUrl} alt={selected.title} />
            <LightboxInfo>
              <LightboxTitle>"{selected.title}"</LightboxTitle>
              <LightboxArtist>
                🏴‍☠️ by {selected.childName}, age {selected.age}
              </LightboxArtist>
              {selected.description && (
                <LightboxDescription>{selected.description}</LightboxDescription>
              )}
              <CardDate>Entered {formatDate(selected.reviewedAt || selected.submittedAt)}</CardDate>
            </LightboxInfo>
          </LightboxContent>
        </Lightbox>
      )}

      <Footer />
    </PageContainer>
  );
};

export default ChildrensArtGallery;
